import React, { useRef, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import Icon from 'components/commons/Icon';
import { useOnClickOutside } from 'hooks';
import PropTypes from 'prop-types';

import {
  Select,
  OutlinedSelect,
  DropdownListWrapper,
  DropdownListHeader,
  DropdownContent,
  DropdownContentList,
  DropdownItem,
  DesktopSearchInputWrapper,
  DesktopSearchInput,
  DropdownName,
  DropdownToggle,
  DropdownLink,
  TitleExtractor,
  CloseButton,
} from './styles';

const SelectComponent = ({
  options,
  value,
  onChange,
  titleExtractor,
  labelExtractor,
  type,
  prefixIcon,
  filterFunction,
  isFilterable,
  isButtonMode,
  renderSelectItem,
  getFullOptionValue,
}) => {
  const refButton = useRef();

  const [searchValue, setSearchValue] = useState('');
  const [isOpen, setOpenStatus] = useState(false);

  useOnClickOutside(refButton, () => {
    if (isOpen) {
      setOpenStatus(false);
    }
  });

  const ContainerComponent = useMemo(() => {
    if (isButtonMode) {
      // eslint-disable-next-line react/prop-types
      return ({ children, ...props }) => (
        <>
          <DropdownToggle type="button" aria-label="dropdown" {...props}>
            <Icon
              name="arrow"
              style={
                isOpen
                  ? {
                      transform: 'rotate(180deg)',
                    }
                  : null
              }
            />
            {children}
          </DropdownToggle>
        </>
      );
    }
    if (type === 'outline') {
      return OutlinedSelect;
    }
    return Select;
  }, []);

  const onSearchInputChange = event => {
    const { value } = event.target;

    setSearchValue(value);
  };

  const computedOptions = useMemo(() => {
    if (!searchValue) {
      return options;
    }

    const search = searchValue.toLowerCase();

    return options.filter(item => filterFunction(item, search));
  }, [options, searchValue]);

  const renderTitle = () => {
    if (isButtonMode) {
      return null;
    }

    return (
      <>
        {prefixIcon && <Icon name={prefixIcon} style={{ marginRight: 8 }} />}

        {titleExtractor ? (
          <TitleExtractor>
            <FormattedMessage id={titleExtractor(value)} />
          </TitleExtractor>
        ) : null}
      </>
    );
  };

  const renderItemContent = item =>
    renderSelectItem ? (
      renderSelectItem(item)
    ) : (
      <DropdownName>
        {labelExtractor ? (
          <FormattedMessage id={labelExtractor(item.label)} />
        ) : (
          item.label
        )}
      </DropdownName>
    );

  const handleClose = () => {
    setOpenStatus(false);
  };

  return (
    <div ref={refButton} style={{ position: 'relative' }}>
      <ContainerComponent
        isOpen={isOpen}
        onClick={() => setOpenStatus(!isOpen)}
      >
        {renderTitle()}

        {!isButtonMode && (
          <Icon
            name="arrow"
            style={
              isOpen
                ? {
                    marginLeft: 'auto',
                    transform: 'rotate(180deg)',
                  }
                : { marginLeft: 'auto' }
            }
          />
        )}
      </ContainerComponent>

      {isOpen && (
        <DropdownListWrapper>
          {isButtonMode && (
            <CloseButton onClick={handleClose}>
              <Icon name="close" />
            </CloseButton>
          )}

          {isFilterable && (
            <DropdownListHeader>
              <DesktopSearchInputWrapper>
                <Icon name="search" style={{ marginRight: '11px' }} />
                <DesktopSearchInput
                  type="text"
                  value={searchValue}
                  onChange={onSearchInputChange}
                />
              </DesktopSearchInputWrapper>
            </DropdownListHeader>
          )}

          <DropdownContent>
            <DropdownContentList>
              {computedOptions.map(item => (
                <DropdownItem
                  key={item.value}
                  isActive={item.value === value}
                  onClick={e => {
                    e.stopPropagation();
                    setOpenStatus(false);
                    if (onChange) {
                      onChange(getFullOptionValue ? item : item.value);
                    }
                  }}
                >
                  {item.path ? (
                    <DropdownLink
                      href={`https://${process.env.APP_DOMAIN}${item.path}`}
                      target="_blank"
                    >
                      {renderItemContent(item)}
                    </DropdownLink>
                  ) : (
                    renderItemContent(item)
                  )}

                  {item.value === value && (
                    <Icon name="check" style={{ marginLeft: 'auto' }} />
                  )}
                </DropdownItem>
              ))}
            </DropdownContentList>
          </DropdownContent>
        </DropdownListWrapper>
      )}
    </div>
  );
};

export default SelectComponent;

SelectComponent.propTypes = {
  options: PropTypes.arrayOf(PropTypes.shape({})),
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func,
  titleExtractor: PropTypes.func,
  labelExtractor: PropTypes.func,
  type: PropTypes.string,
  prefixIcon: PropTypes.string,
  isFilterable: PropTypes.bool,
  filterFunction: PropTypes.func,
  isButtonMode: PropTypes.bool,
  renderSelectItem: PropTypes.func,
  getFullOptionValue: PropTypes.bool,
};

SelectComponent.defaultProps = {
  options: null,
  value: null,
  labelExtractor: null,
  titleExtractor: null,
  type: null,
  prefixIcon: null,
  isFilterable: false,
  isButtonMode: false,
  filterFunction: ({ label }, search) => label.toLowerCase().includes(search),
  renderSelectItem: null,
  onChange: null,
  getFullOptionValue: false,
};
